import { useState } from "react";
import { EntityType } from "@common/Common.types.ts";
import { useRelationHook, useRelationsQuery, useRemoveRelation } from "@common/hooks/Relations.hooks.ts";
import { useSearchResultList } from "@common/components/search/SearchResultList.hooks.ts";
import { SearchResultViewModel } from "@common/components/search/SearchResultList.types.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { TaskRelationViewModel } from "../../../taskManagement/taskPane/taskRelationsBox/TaskRelationsBox.types.ts";

export type EntityRelationsTableRow = TaskRelationViewModel & { entityTypeName: string };

export function useEntityRelationsTable(entityId: string, entityType: EntityType) {
    const { translateEntityType } = useTranslation();
    const { platformFeatures } = usePlanContext();

    const { isLoading, data, refetch } = useRelationsQuery(entityId);
    const { removeRelation } = useRemoveRelation(entityId, data);
    const { onItemSelected } = useRelationHook(entityId, entityType, refetch);
    const {
        entitySearchSuggestions,
        isLoading: isLoadingRelationSuggestions,
        isEntitySpecificSearch,
        fetchSuggestions,
        initSuggestions,
        onShowMoreForEntityTypeClick,
        setShowMoreForEntityType,
    } = useSearchResultList(false);
    const [relationSearchQuery, setRelationSearchQuery] = useState("");

    const isShowingUnfilteredSuggestions = relationSearchQuery === "" && !isEntitySpecificSearch && entitySearchSuggestions.length > 0;

    const onRelationSearchFocus = () => {
        // Reopening the field would show the same unfiltered suggestions, so only a narrowed or never-loaded list is fetched again.
        if (isShowingUnfilteredSuggestions) {
            return;
        }
        setShowMoreForEntityType(undefined);
        setRelationSearchQuery("");
        initSuggestions();
    };

    const onRelationSearchChange = (query: string) => {
        setRelationSearchQuery(query);
        fetchSuggestions(query);
    };

    const onRelationSelected = (relation: SearchResultViewModel) => {
        onItemSelected(relation.entityId, relation.entityType);
        // The select owns its open state and closes when the field loses focus.
        (document.activeElement as HTMLElement | null)?.blur();
    };

    const rows: Array<EntityRelationsTableRow> = (data ?? []).map((relation) => ({
        ...relation,
        entityTypeName: translateEntityType(EntityType[relation.entityType].toString()),
    }));

    return {
        isLoading,
        rows,
        hasRelations: rows.length > 0,
        canAccessRelations: platformFeatures.canAccessRelationsBetweenEntities,
        removeRelation,
        relationSuggestions: entitySearchSuggestions,
        isLoadingRelationSuggestions,
        isEntitySpecificSearch,
        onRelationSearchFocus,
        onRelationSearchChange,
        onShowMoreRelationsClick: (type: EntityType | undefined) => onShowMoreForEntityTypeClick(relationSearchQuery, type),
        onRelationSelected,
    };
}
